import React, { useCallback, useEffect, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { IZnsTemplate } from "../../types/znsTemplate";

/** Nominal size of the preview content (typical mobile viewport); used to scale-to-fit. */
const PREVIEW_NOMINAL_WIDTH = 380;
const PREVIEW_NOMINAL_HEIGHT = 640;

/** Props for MessageDetail: a ZNS template and optional dialog title. */
export interface MessageDetailProps {
  /** The ZNS template; only previewUrl is used to embed the preview. */
  template: IZnsTemplate;
  /** Called when the user clicks Close; use to close the modal. */
  onClose?: () => void;
}

/**
 * Renders message/template preview: optional title and an iframe embedding template.previewUrl.
 * The iframe is scaled to fit the container so content fits the modal without overflow.
 */
const MessageDetail = ({ template, onClose }: MessageDetailProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const updateScale = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    const scaleX = w / PREVIEW_NOMINAL_WIDTH;
    const scaleY = h / PREVIEW_NOMINAL_HEIGHT;
    setScale(Math.min(1, scaleX, scaleY));
  }, []);

  useEffect(() => {
    updateScale();
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScale);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateScale, template.previewUrl]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        padding: "8px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ flexShrink: 0 }}>
            {template.templateName}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              flexShrink: 0,
              fontSize: 14,
              color: "rgba(0, 0, 0, 0.6)",
              fontWeight: 400,
            }}
          >
            {`Template ID: ${template.templateId}`}
          </Typography>
        </Box>
        <Box>
          {onClose && (
            <IconButton
              onClick={onClose}
              aria-label="Close"
              sx={{
                width: 48,
                height: 48,
                bgcolor: "transparent",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <CloseIcon sx={{ fontSize: 40 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          overflow: "hidden",
          bgcolor: "#efefef",
          border: "1px solid",
          borderColor: "primary.main",
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            width: PREVIEW_NOMINAL_WIDTH,
            height: PREVIEW_NOMINAL_HEIGHT,
            flexShrink: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top center",
            bgcolor: "#efefef",
          }}
        >
          <Box
            component="iframe"
            src={template.previewUrl}
            title={template.templateName}
            sx={{
              width: PREVIEW_NOMINAL_WIDTH,
              height: PREVIEW_NOMINAL_HEIGHT,
              border: "none",
              borderRadius: 1,
              display: "block",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MessageDetail;
